import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { DefaultPosition } from '@sorare/core/src/assets/icons/DefaultPosition';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import type { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { arrayToObject, range } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { getSortedAppearancesFromRules } from '@sorare/core/src/lib/players';

import { AppearanceRuleIcon } from 'components/lineup/AppearanceRuleIcon';
import { PlayerCard } from 'components/lineup/PlayerCard';
import { SimpleCardPlaceholder } from 'components/lineup/SimpleCardPlaceholder';
import { So5AppearanceDialog } from 'components/stats/So5AppearanceDialog';
import {
  getComposeTeamRoute,
  isAppearanceLocked,
  isFixtureLive,
} from 'lib/so5';

import type { So5Lineup_so5LeaderboardContender } from './__generated__/index.graphql';

const Slot = styled.div`
  margin: 0 auto auto;
  width: 100%;
  height: 100%;
  min-width: 0;
`;
const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-and-a-half-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

type Props = {
  so5LeaderboardContender?: So5Lineup_so5LeaderboardContender;
  lineupStyle?: LineupStyle;
  className?: string;
  fillHeightPercent?: number;
  displayLiveEdit?: boolean;
};

export const So5Lineup = ({
  so5LeaderboardContender,
  lineupStyle,
  className,
  fillHeightPercent,
  displayLiveEdit,
}: Props) => {
  const { isMe } = useCurrentUserContext();
  const openDialog = useOpenDialog();
  const { sport } = useSportContext();
  const [so5Appearance, setSo5Appearance] = useState<
    | NonNullable<
        So5Lineup_so5LeaderboardContender['so5Lineup']
      >['so5Appearances'][0]
    | undefined
  >();
  const withInlineGamesDots =
    so5LeaderboardContender?.so5Leaderboard.so5Fixture.sport !== Sport.FOOTBALL;

  const { formatMessage } = useIntl();
  const { so5Lineup, so5Leaderboard, managerTeam } =
    so5LeaderboardContender || {};

  const appearances =
    so5Leaderboard &&
    getSortedAppearancesFromRules({
      so5Lineup: so5Lineup || undefined,
      so5Leaderboard,
    });
  const card =
    so5Appearance && 'anyCard' in so5Appearance
      ? so5Appearance.anyCard
      : undefined;

  const eligibleCardsCountByPosition = arrayToObject({
    value: c => c,
    key: c => c.position,
    list: so5Leaderboard?.eligibleCardsCountByPosition || [],
  }) as Record<
    Position,
    So5Lineup_so5LeaderboardContender['so5Leaderboard']['eligibleCardsCountByPosition'][number]
  >;

  const userSlug = managerTeam?.user?.slug || so5Lineup?.user.slug;

  const canLiveEditLineup =
    displayLiveEdit &&
    isMe(userSlug) &&
    so5Lineup?.updatable &&
    isFixtureLive(so5Leaderboard?.so5Fixture) &&
    so5Leaderboard?.canCompose?.value &&
    !so5Lineup?.cancelledAt &&
    !appearances?.every(
      a => isType(a, 'So5Appearance') && isAppearanceLocked(a)
    );

  const numberOfPlaceholderNeeded = sport === Sport.BASEBALL ? 7 : 5;

  return (
    <>
      <LineupCardsLayout
        fillHeightPercent={fillHeightPercent}
        lineupStyle={lineupStyle}
        slots={appearances?.length}
        className={className}
        cancelled={!!so5Lineup?.cancelledAt}
        gap={lineupStyle !== 'spread' ? 0.5 : 1}
      >
        {appearances?.map((appearance, i) => {
          const appearanceRule = so5Leaderboard?.rules?.appearances?.at(i);

          if (!appearanceRule) {
            return null;
          }

          const position =
            (appearanceRule.positions.length === 1 &&
              appearanceRule.positions[0]) ||
            null;

          const count =
            position && eligibleCardsCountByPosition[position]
              ? eligibleCardsCountByPosition[position].totalCount -
                eligibleCardsCountByPosition[position].usedCardsCount
              : undefined;

          const composeTeamPath = so5Leaderboard?.slug
            ? getComposeTeamRoute({
                leaderboardSlug: so5Leaderboard?.slug,
                eventType: so5Leaderboard?.so5Fixture.type.toLowerCase(),
                managerTeamId: managerTeam?.id,
                lineupId: so5Lineup?.id,
                sport,
              })
            : '';

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Slot key={i}>
              {'index' in appearance ? (
                <PlayerCard
                  appearance={appearance}
                  showScore
                  withDate
                  withInlineGamesDots={withInlineGamesDots}
                  onClick={() => {
                    setSo5Appearance(appearance);
                  }}
                  Footer={SlotFooter}
                  displayLiveEdit={canLiveEditLineup}
                  editPath={composeTeamPath}
                />
              ) : (
                <Link
                  aria-label={formatMessage(fantasy.composeLineup)}
                  to={getComposeTeamRoute({
                    leaderboardSlug: so5Leaderboard?.slug || '',
                    lineupId: so5Lineup?.id,
                    managerTeamId: managerTeam?.id,
                    sport,
                    eventType:
                      so5Leaderboard?.so5Fixture.type.toLowerCase() || '',
                  })}
                >
                  <SimpleCardPlaceholder
                    icon={
                      <AppearanceRuleIcon appearanceRule={appearanceRule} />
                    }
                    label=""
                    count={count}
                    loading={!so5LeaderboardContender}
                    Footer={lineupStyle === 'spread' ? SlotFooter : undefined}
                  />
                </Link>
              )}
            </Slot>
          );
        }) ||
          range(numberOfPlaceholderNeeded).map(i => (
            <Slot key={i}>
              <SimpleCardPlaceholder
                icon={<DefaultPosition />}
                label=""
                Footer={lineupStyle === 'spread' ? SlotFooter : undefined}
                loading={false}
              />
            </Slot>
          ))}
      </LineupCardsLayout>
      {!!so5Appearance && (
        <So5AppearanceDialog
          open
          onClose={() => setSo5Appearance(undefined)}
          showUpsell={!card}
          so5AppearanceId={so5Appearance.id}
          onClickGame={id =>
            openDialog(DialogKey.game, id, {
              additionalParams: {
                [DialogKey.eventType]:
                  so5LeaderboardContender?.so5Leaderboard.so5Fixture.type || '',
              },
            })
          }
        />
      )}
    </>
  );
};

So5Lineup.fragments = {
  so5LeaderboardContender: gql`
    fragment So5Lineup_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        updatable
        cancelledAt
        so5Appearances {
          id
          index
          position
          so5Score {
            id
          }
          anyCard {
            slug
          }
          ...PlayerCard_so5Appearance
        }
        user {
          slug
        }
        ...getSortedAppearancesFromRules_so5Lineup
      }
      managerTeam {
        id
        user {
          slug
        }
      }
      so5Leaderboard {
        slug
        mainRarityType
        canCompose {
          value
        }
        so5Fixture {
          slug
          type
          sport
          ...isFixtureLive_so5Fixture
        }
        rules {
          id
          appearances {
            id
            name
            positions
            ...AppearanceRuleIcon_appearanceRule
          }
        }
        eligibleCardsCountByPosition {
          slug
          position
          totalCount
          usedCardsCount
        }
        ...getSortedAppearancesFromRules_so5Leaderboard
      }
    }
    ${PlayerCard.fragments.so5Appearance}
    ${getSortedAppearancesFromRules.fragments.so5Lineup}
    ${getSortedAppearancesFromRules.fragments.so5Leaderboard}
    ${AppearanceRuleIcon.fragments.appearanceRule}
    ${isFixtureLive.fragments.so5Fixture}
  ` as TypedDocumentNode<So5Lineup_so5LeaderboardContender>,
};
